import { zodResolver } from '@hookform/resolvers/zod';
import { FormContainer, Row } from '@wyz/ui';
import { invariant } from '@wyz/utils';
import { BaseSyntheticEvent } from 'react';
import { useForm } from 'react-hook-form';
import { Outlet } from 'react-router';
import { useManageQuoteSetting } from '~/purchase/api';
import { usePurchaseContext, useQuoteCartContext } from '~/purchase/context';
import { QuoteHelpers } from '~/purchase/helpers';
import { generateQuoteSettingSchema } from '~/purchase/schemas';
import { IQuoteCartForm, IQuoteSetting } from '~/purchase/types';

type Props = {
  setting: IQuoteSetting;
};

const SAVE_AND_SEND_BUTTON_ID = 'save-and-send';
const PRINT_BUTTON_ID = 'print';

function QuoteCartForm({ setting }: Props) {
  const { state } = usePurchaseContext();
  const quoteCartContext = useQuoteCartContext();
  const targetCustomer = state.targetCustomer;
  invariant(targetCustomer);
  const quoteSettingManagement = useManageQuoteSetting({
    get: { enabled: true },
  });
  const methods = useForm<IQuoteCartForm>({
    resolver: zodResolver(
      generateQuoteSettingSchema(quoteSettingManagement.get.data?.data),
    ),
    defaultValues: {
      products: quoteCartContext.data.defaultProducts,
      rimType: quoteCartContext.data.defaultRimType,
      comment: setting.resolvedAdditionalMessage,
      validityDays: setting.validityDays,
    },
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data, event) => {
        const buttonId =
          (
            event as BaseSyntheticEvent<SubmitEvent> | undefined
          )?.nativeEvent.submitter?.getAttribute('id') ?? undefined;
        const payload = QuoteHelpers.prepareSubmitCartPayload(data);

        if (buttonId === SAVE_AND_SEND_BUTTON_ID) {
          quoteCartContext.actions.submit.process({
            payload,
            sendMail: true,
            print: false,
            customerId: targetCustomer.id,
          });
        } else if (buttonId === PRINT_BUTTON_ID) {
          quoteCartContext.actions.submit.process({
            payload,
            sendMail: false,
            print: true,
            customerId: targetCustomer.id,
          });
        } else {
          quoteCartContext.actions.submit.process({
            payload,
            sendMail: false,
            print: false,
            customerId: targetCustomer.id,
          });
        }
      }}
    >
      <Row>
        <Outlet />
      </Row>
    </FormContainer>
  );
}

export default QuoteCartForm;
